import {Link} from "react-router-dom";
import React, {useEffect, useState, useMemo, useRef} from "react";
import {flattenStructure} from "../utils/siteStructure";
import siteStructure from "../siteStructure.json";
import {Input} from 'antd';
import {SearchOutlined, CloseCircleFilled} from '@ant-design/icons';
import {TransitionGroup, CSSTransition} from 'react-transition-group';
import {useNews} from "../news/NewsContext";
import {useTheme} from "./Header";


// Картинки теперь в public → строковые URL.
// Логика: если item.previewText задан — значит item.image это уже готовый URL (например, из API).
// Иначе считаем, что item.image — относительный путь от public/images/.
const getImage = (item) => {
    if (!item || !item.image) return "";
    if (item.previewText) {
        return item.image;
    }
    const clean = String(item.image).replace(/^\.?\//, "");
    return `/images/${clean}`;
}

function searchInAllData(menuData, newsData, searchQuery) {

    const results = [];

    if (!searchQuery) {
        return results
    }

    const searchTerms = searchQuery.split(',')
        .map(term => term.trim().toLowerCase())
        .filter(term => term.length > 0);

    if (searchTerms.length === 0) return results;

    // Поиск в меню (по полю title)
    for (const key in menuData) {
        const item = menuData[key];
        if (item.title && searchTerms.some(term =>
            item.title.toLowerCase().includes(term)
        )) {
            results.push(item);
        }
    }

    // Поиск в новостях (по полю title)
    if (Array.isArray(newsData)) {
        const searchedNews = newsData.filter(item =>
            item.title && searchTerms.some(term =>
                item.title.toLowerCase().includes(term)
            )
        );
        results.push(...searchedNews)
    }

    return results
}

// Компонент для отображения результатов поиска
export function Search({searchState, setSearchState}) {

    const { newsData } = useNews();
    const { theme } = useTheme()
    const [menuData] = useMemo(() => {
        let headerLinks = ["about", "services", "production", "lizing", "news"]
        let structure = flattenStructure(siteStructure)
        headerLinks.forEach(link => delete structure[link])
        return [structure];
    }, []);

    const [inputValue, setInputValue] = useState("");

    const searchResults = useMemo(() => {
        return searchInAllData(menuData, newsData, inputValue);
    }, [inputValue, menuData, newsData]);

    const [isInputFocused, setIsInputFocused] = useState(false);

    return (
        <div className={`searchBackground ${theme} ${!searchState ? "hideSearch" : ""} headerSearch`}>
            {
                theme === "light"?
                    <div className={`complexBack ${theme}`}> </div>
                    : null
            }
            <div className={`${searchState ? "searchContent" : ""}`}>
                <SearchInput inputValue={inputValue} setInputValue={setInputValue} searchState={searchState}
                             searchResults={searchResults} setSearchState={setSearchState}
                             isInputFocused={isInputFocused} setIsInputFocused={setIsInputFocused} theme={theme}/>
                <SearchItems inputValue={inputValue} searchResults={searchResults} setSearchState={setSearchState}
                             isInputFocused={isInputFocused} theme={theme}/>
            </div>
        </div>
    );
}

function SearchInput({
                         inputValue,
                         setInputValue,
                         searchState,
                         searchResults,
                         setSearchState,
                         isInputFocused,
                         setIsInputFocused,
                         theme
                     }) {

    const searchContainerRef = useRef(null);
    const inputRef = useRef(null);
    const highlightStyle = theme === 'dark' ? {color: '#fff', fontWeight: 'bold'} : {color: '#272a2f', fontWeight: 'bold'};

    function highlightSearchTerm(str, searchTerm, length = 20, highlightStyle = {color: '#fff', fontWeight: 'bold'}) {
        const lowerStr = str.toLowerCase();
        const lowerSearchTerm = searchTerm.toLowerCase();
        const index = lowerStr.indexOf(lowerSearchTerm);

        if (index === -1) return <span>{str}</span>;

        let start = Math.max(0, index - length);
        let end = Math.min(str.length, index + searchTerm.length + length);

        while (start > 0 && !/\s/.test(str[start - 1])) start--;
        while (end < str.length && !/\s/.test(str[end])) end++;

        const beforeMatch = start > 0 ? "..." + str.slice(start, index) : str.slice(start, index);
        const matchPart = str.slice(index, index + searchTerm.length);
        const afterMatch = end < str.length ? str.slice(index + searchTerm.length, end) + "..." : str.slice(index + searchTerm.length, end);

        return (
            <span>
            {beforeMatch}
                <span style={highlightStyle}>{matchPart}</span>
                {afterMatch}
        </span>
        );
    }

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === "Escape" && searchState) {
                setSearchState(false);
                setIsInputFocused(false);
                if (inputRef.current) {
                    inputRef.current.blur();
                }
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [searchState, setSearchState, setIsInputFocused]);

    useEffect(() => {
        if (searchState && inputRef.current) {
            inputRef.current.focus();
            setIsInputFocused(true);
        }
    }, [searchState, setIsInputFocused]);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.target.blur();
            setIsInputFocused(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchContainerRef.current &&
                !searchContainerRef.current.contains(event.target) &&
                !event.target.closest('.menuManyObj-item')
            ) {
                setIsInputFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setIsInputFocused]);

    return (
        <div className={`searchInput ${theme} ${isInputFocused ? "searchInputFocus" : ""}`} ref={searchContainerRef}>
            <Input
                ref={inputRef}
                placeholder="Найти..."
                allowClear={{
                    clearIcon: <CloseCircleFilled style={{fontSize: '18px', color: '#ff4d4f'}}/>
                }}
                size="large"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsInputFocused(true)}
                className="search-input-no-hover"
                prefix={
                    <SearchOutlined
                        style={{
                            color: theme === "dark"
                                ? (isInputFocused ? '#fff' : 'rgb(118, 118, 118)')
                                : (isInputFocused ? '#000' : '#272a2f'),
                            transform: "scale(1.1) scaleX(-1)",
                            transition: 'color 0.3s ease',
                        }}
                    />
                }
                style={{
                    backgroundColor: 'transparent',
                }}
            />

            <div
                className={`search-results-container ${isInputFocused && searchResults.length > 0 ? "search-results-container-focus" : ""}`}>
                {searchResults.slice(0, 8).map((item) => (
                    <div key={item.url} className={`search-result-item ${theme}`}>
                        <Link to={item.url} onClick={() => {
                            setIsInputFocused(false);
                            setSearchState(false);
                        }}>
                            {highlightSearchTerm(item.title, inputValue, 20, highlightStyle)}
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
}


function SearchItems({inputValue, searchResults, setSearchState, isInputFocused, theme}) {

    const TransitionWrapper = ({children, in: _in, onExited: _onExited, ...props}) => {
        return React.cloneElement(children, props);
    };

    return (
        <>
            <div
                className={`menuManyObj-container`}
                style={{
                    transform: `translateY(${isInputFocused ? searchResults.slice(0, 8).length * 30 : 0}px)`,
                    transition: `transform 0.5s ease ${isInputFocused ? "0s" : "0.2s"}`,
                }}
            >
                <TransitionGroup component={null}>
                    {searchResults.slice(0, 8).map((item) => (
                        <CSSTransition
                            key={item.url}
                            timeout={300}
                            classNames="search-item"
                        >
                            <TransitionWrapper>
                                <Link
                                    to={item.url}
                                    className={`menuManyObj-item`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setSearchState(false)
                                    }}
                                >
                                    <div className={`menuManyObj-image-container ${theme}`}>
                                        <img
                                            src={getImage(item)}
                                            alt={item.title}
                                            className={`menuManyObj-image`}
                                        />
                                    </div>
                                    <div className={`menuManyObjText ${theme}`}>{item.title}</div>
                                </Link>
                            </TransitionWrapper>
                        </CSSTransition>
                    ))}
                    <CSSTransition
                        timeout={300}
                        classNames="search-item"
                    >
                        <div className={`searchMessage ${theme} ${searchResults.length === 0 && inputValue !== "" ? "searchMessageVisible" : ""}`}>
                            Ничего не найдено
                        </div>
                    </CSSTransition>
                </TransitionGroup>
            </div>

        </>
    );
}